import { faApple } from '@fortawesome/free-brands-svg-icons';
import {
  faFileChartColumn, faGears, faLocationCircle, faStreetView, faTools, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import type { MapInfo } from '~/_shared/types/map';
import { type TranslationFnc } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type SidebarRoundMenuButtonProps } from '~/sidebar/sidebarApps/rightSidebar/roundButtonMenu/sidebarRoundButtonMenu.types';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import { MenuButtons } from '../customizeLocationPanel.enums';
import {
  type EditLocationCallbackProps, type RemoveLocationCallbacks, type ToolLocationCallbackProps,
} from './contextMenuStructure/contextMenuStructure.types';
import { deleteMarkerContextMenuStructure } from './contextMenuStructure/structures/deleteMarkerContextMenuStructure';
import { editLocationContextMenuStructure } from './contextMenuStructure/structures/editLocationContextMenuStructure';
import { toolsContextMenuStructure } from './contextMenuStructure/structures/toolsContextMenuStructure';
import { type LocationMenuRefs } from './roundButtonMenu.types';

export const rightLocationMenu = (props: {
  buttonRefs: LocationMenuRefs;
  onMenuSelect: (button: MenuButtons | null) => void; // this determines which round button will be highlighted as selected
  editLocationCallbacksProps: EditLocationCallbackProps;
  removeLocationCallbacks: RemoveLocationCallbacks;
  toolLocationCallbacks: ToolLocationCallbackProps;
  t: TranslationFnc;
  isAppleMobileDevice: boolean;
  isEditingDisabled: boolean;
  isMobileDevice: boolean;
  isMobileScreen: boolean;
  isLayeredMapConnected: boolean;
  isSpreadsheetEditDisabled: boolean;
  mapInfo: MapInfo | null;
}): ReadonlyArray<SidebarRoundMenuButtonProps<MenuButtons>> => {
  const { t, toolLocationCallbacks: { addToRouteCallback, drawRadiusCallback } } = props;

  const editingDisabledButtonStyle = props.isEditingDisabled ? RoundButtonStyle.Disabled : RoundButtonStyle.Primary;
  const isSalesforceMap = checkIsSalesforceMap(props.mapInfo);
  const isDeleteDisabled = props.isEditingDisabled || props.isLayeredMapConnected || isSalesforceMap;

  const deleteButtonTooltip = isSalesforceMap ? t('salesforce.spreadsheetData.cannotEditInApp') : t(MenuButtons.DeleteMarker);

  const isNavigateGoogleEnabled = props.isMobileDevice && props.toolLocationCallbacks.navigate;
  const isNavigateAppleActive = props.isAppleMobileDevice && props.toolLocationCallbacks.navigateApple;
  const isViewSummaryInMainMenu = !isNavigateGoogleEnabled && !isNavigateAppleActive;

  return [
    isNavigateGoogleEnabled ? {
      id: 'navigateToGoogle',
      buttonName: MenuButtons.NavigateToGoogle,
      buttonStyle: RoundButtonStyle.Primary,
      icon: faLocationCircle,
      tooltipLabel: t(MenuButtons.NavigateToGoogle),
      onClick: props.toolLocationCallbacks.navigate?.callback,
    } : null,
    isNavigateAppleActive ? {
      id: 'navigateToApple',
      buttonName: MenuButtons.NavigateToApple,
      buttonStyle: RoundButtonStyle.Primary,
      icon: faApple,
      tooltipLabel: t(MenuButtons.NavigateToApple),
      onClick: props.toolLocationCallbacks.navigateApple?.callback,
    } : null,
    !props.isMobileDevice && props.toolLocationCallbacks.viewSummaryCallback && isViewSummaryInMainMenu ? {
      id: 'viewSummary',
      buttonName: MenuButtons.DetailedReport,
      buttonStyle: props.toolLocationCallbacks.viewSummaryDisabled?.isDisabled ? RoundButtonStyle.Disabled : editingDisabledButtonStyle,
      icon: faFileChartColumn,
      tooltipLabel: t(MenuButtons.DetailedReport),
      onClick: props.toolLocationCallbacks.viewSummaryCallback,
      disabled: props.toolLocationCallbacks.viewSummaryDisabled,
    } : null,
    props.isEditingDisabled ? null : {
      id: 'settingsAndCustomization',
      buttonName: MenuButtons.SettingsAndCustomization,
      buttonStyle: editingDisabledButtonStyle,
      contextItemMenuStructure: editLocationContextMenuStructure({
        viewSummary: !isViewSummaryInMainMenu && props.toolLocationCallbacks.viewSummaryCallback ? {
          callback: props.toolLocationCallbacks.viewSummaryCallback,
          isDisabled: props.toolLocationCallbacks.viewSummaryDisabled?.isDisabled,
          disabledTooltip: props.toolLocationCallbacks.viewSummaryDisabled?.reason,
        } : undefined,
        categorizeData: props.editLocationCallbacksProps.categorizeData,
        editData: props.editLocationCallbacksProps.editData,
        editImages: props.editLocationCallbacksProps.editImages,
        editMarker: props.editLocationCallbacksProps.editMarker,
        editPanel: {
          callback: () => {
            props.onMenuSelect(MenuButtons.SettingsAndCustomization);
            props.editLocationCallbacksProps.editPanel.callback();
          },
          isDisabled: props.editLocationCallbacksProps.editPanel.isDisabled,
          disabledTooltip: props.editLocationCallbacksProps.editPanel.disabledTooltip,
        },
      }, props.t, props.isMobileScreen),
      icon: faGears,
      ref: props.buttonRefs.editLocation,
      tooltipLabel: t(MenuButtons.SettingsAndCustomization),
      notSelectable: props.isEditingDisabled,
    },
    (addToRouteCallback || drawRadiusCallback) ? {
      id: 'tools',
      buttonName: MenuButtons.Tools,
      buttonStyle: RoundButtonStyle.Primary,
      contextItemMenuStructure: toolsContextMenuStructure({
        addDTPCallback: props.toolLocationCallbacks.addDTPCallback,
        addToRouteCallback: props.toolLocationCallbacks.addToRouteCallback,
        drawRadiusCallback: props.toolLocationCallbacks.drawRadiusCallback,
      }, props.t),
      icon: faTools,
      ref: props.buttonRefs.tools,
      tooltipLabel: t(MenuButtons.Tools),
    } : null,
    props.isSpreadsheetEditDisabled ? null : {
      id: 'deleteMarker',
      buttonName: MenuButtons.DeleteMarker,
      buttonStyle: isDeleteDisabled ? RoundButtonStyle.Disabled : RoundButtonStyle.Danger,
      contextItemMenuStructure: deleteMarkerContextMenuStructure({
        deleteMarkerCallback: () => props.removeLocationCallbacks.removeMarkerCallback(),
      }, props.t),
      icon: faTrash,
      notSelectable: isDeleteDisabled,
      ref: props.buttonRefs.deleteMarker,
      tooltipLabel: deleteButtonTooltip,
    },
  ].filter(notNullsy);
};

export const leftLocationMenu = ({ onMenuSelect, isActive, t }: {
  onMenuSelect: (button?: MenuButtons | null) => void;
  isActive: boolean;
  t: TranslationFnc;
}) => [
  {
    id: 'streetView',
    buttonName: MenuButtons.StreetView,
    buttonStyle: isActive ? RoundButtonStyle.Quaternary : RoundButtonStyle.Disabled,
    icon: faStreetView,
    notSelectable: !isActive,
    onClick: () => onMenuSelect(),
    tooltipLabel: isActive ? t(MenuButtons.StreetView) : t('Street View Not Available'),
  } satisfies SidebarRoundMenuButtonProps<MenuButtons>,
];
